import { Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { MessageModule } from 'primeng/message';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { MessageService } from 'primeng/api';
import {
  SolCodeService,
  SolCodeMfa,
  SolCodeMfaSave,
  SolCodeStatus,
  MfaPerson,
  OperationalSite,
} from '../../../backoffice/services/sol-code.service';
import { RegistrationStepService } from '../../../core/services/registration-step.service';
import { UserSessionService } from '../../../core/services/mock-auth.service';
import { ProfessionalsService } from '../../../core/services/professionals.service';
import { GeographyService } from '../../../core/services/geography.service';
import { FormDropdownService } from '../../../core/services/form-dropdown.service';
import { ConfirmationDialogService } from '../../../core/services/confirmation-dialog.service';

/** Riga editabile di associazione persona ↔ codice SOL (fase MFA). */
interface MfaAssocForm {
  collaboratorId: number;
  firstName: string;
  lastName: string;
  /** Cod. fiscale precompilato dall'anagrafica del collaboratore / impiegato (sola lettura). */
  fiscalCode: string | null;
  role: string | null;
  kind: 'COLLABORATOR' | 'EMPLOYEE';
  selected: boolean;
  accessEmail: string;
  /** True quando l'utente ha lasciato il campo mail: abilita l'errore di formato prima del submit. */
  accessEmailTouched: boolean;
  /**
   * True quando l'associazione è già stata VALIDATA (stato ACTIVE) da una precedente approvazione MFA:
   * è bloccata (sola lettura), non deselezionabile né modificabile; si possono solo aggiungere NUOVE
   * associazioni accanto. Vale in particolare per il primo impianto (SOL già attivi).
   */
  locked: boolean;
}

/** Dati anagrafici del titolare del fiduciario (persona fisica o rappresentante legale). */
interface TitolareData {
  firstname: string;
  lastname: string;
  fiscalCode: string;
}

/** Stato editabile dei dati MFA di un singolo codice SOL. */
interface MfaSolForm {
  solCodeId: number;
  code: string;
  master: boolean;
  status: SolCodeStatus;
  /** Denominazione assegnata al SOL lato Back Office (sola lettura, aiuta l'identificazione). */
  denomination: string | null;
  /** True se il SOL è su sede fisica; false se su territorio. */
  physicalSite: boolean;
  region: string | null;
  province: string | null;
  /** Sede associata (campi indirizzo grezzi); il tipo via viene decodificato per il display. */
  site: OperationalSite | null;
  principalEmail: string;
  holderFirstName: string;
  holderLastName: string;
  holderFiscalCode: string;
  associations: MfaAssocForm[];
  // ── Stato UI (non inviato al backend) ──────────────────────────────────────
  /** Sezione dati principali (step 1) collassata dall'utente. */
  collapsed0: boolean;
  /** Sezione associazioni (step 2) collassata dall'utente. */
  collapsed1: boolean;
  /** Sezione riepilogo (step 3) collassata dall'utente. */
  collapsed2: boolean;
  /** Testo di ricerca persone nello step 2 (filtro per nome/cognome/CF). */
  search: string;
  /** Mostra solo le persone selezionate nello step 2. */
  onlySelected: boolean;
  /** True quando l'utente ha lasciato il campo mail principale: abilita l'errore di formato prima del submit. */
  principalEmailTouched: boolean;
}

/**
 * Wizard MFA (Codici SOL) — lato fiduciario.
 * Il Back Office apre la compilazione (step MFA_SETUP in USER_INPUT); qui il fiduciario
 * inserisce, per ogni codice SOL: mail principale, responsabile e associazioni delle
 * persone (collaboratori / impiegati amministrativi) con la relativa mail di accesso.
 * Alla conferma i dati vengono salvati e lo step passa in revisione al Back Office.
 */
@Component({
  selector: 'app-professionals-mfa',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    MessageModule,
    ToastModule,
    TagModule,
  ],
  providers: [MessageService],
  templateUrl: './professionals-mfa.component.html',
  styleUrl: './professionals-mfa.component.scss',
})
export class ProfessionalsMfaComponent implements OnInit {
  private solCodeService = inject(SolCodeService);
  private registrationStepService = inject(RegistrationStepService);
  private mockAuthService = inject(UserSessionService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private titleService = inject(Title);
  private professionalsService = inject(ProfessionalsService);
  private geographyService = inject(GeographyService);
  private formDropdownService = inject(FormDropdownService);
  private confirmDialogService = inject(ConfirmationDialogService);

  /** Stesso avviso di "Dati specifici": esci senza salvare = perdi i dati inseriti. */
  private static readonly UNSAVED_CHANGES_MESSAGE =
    'Se cambi sezione o lasci la pagina senza cliccare su "Salva bozza", perderai i dati inseriti. Vuoi procedere?';

  private static readonly EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  /** Formato standard del codice fiscale persona fisica (16 caratteri). */
  private static readonly FISCAL_CODE_RE =
    /^[A-Za-z]{6}[0-9]{2}[A-Za-z][0-9]{2}[A-Za-z][0-9]{3}[A-Za-z]$/;
  /**
   * Commento boilerplate impostato dal BE all'apertura della compilazione
   * (endpoint "Richiedi compilazione MFA"). È ridondante rispetto all'header di
   * pagina e non va mostrato; le vere note di richiesta modifica vanno invece mostrate.
   */
  private static readonly MFA_REQUEST_NOTE = 'Richiesta compilazione dati MFA (codici SOL)';
  // Errori per singolo campo quando la STESSA mail è ripetuta in QUESTO form (tra codici SOL/persone
  // del fiduciario corrente). Sono casi distinti dalla mail "già censita a DB": quella la rileva solo
  // il backend al salvataggio (409 DUPLICATE_RESOURCE), perché il FE non conosce il pool globale.
  // I messaggi sono granulari per rendere esplicito DOVE risolvere il conflitto (priorità step 1 > 2).
  /** Mail principale usata in più codici SOL (ripetizione interna allo step «Dati di accesso Del Legale Rappresentante»). */
  private static readonly DUP_PRINCIPAL_MSG =
    'Questa mail è usata in più codici SOL: assegnane una diversa a ciascuno.';
  /** Mail di accesso ripetuta tra più persone (ripetizione interna allo step «Associazioni»). */
  private static readonly DUP_PEOPLE_MSG =
    'Questa mail è ripetuta tra le persone: assegnane una diversa a ciascuna.';
  /** Mail già in uso da un codice SOL attivo (sola lettura): non può essere riassegnata. */
  private static readonly DUP_ACTIVE_MSG =
    'Questa mail è già in uso da un codice SOL attivo: usane una diversa.';
  /**
   * Errore per singolo campo quando il backend segnala la mail come già presente nel pool
   * globale a DB (altro codice SOL o altro accesso, anche di altri fiduciari): caso distinto
   * dalla semplice ripetizione nel form.
   */
  private static readonly DB_DUPLICATE_EMAIL_MSG =
    'Questa mail risulta già registrata nel sistema (altro codice SOL o accesso): usane una diversa.';

  // Input opzionali per l'embed in sola lettura nel Back Office (review/dettaglio del fiduciario),
  // stesso pattern di professionals-specific-data: id esplicito + modalità readonly.
  professionalIdInput = input<string | null>(null);
  readonly = input<boolean>(false);

  professionalId = signal<string | null>(null);
  isLoading = signal<boolean>(true);
  isSaving = signal<boolean>(false);
  isConfirming = signal<boolean>(false);
  loadError = signal<boolean>(false);

  /** true quando lo step corrente è MFA_SETUP in USER_INPUT (compilazione consentita). */
  editable = signal<boolean>(false);
  /** Nota/commento dello step corrente (richiesta compilazione o richiesta modifica). */
  stepNote = signal<string | null>(null);
  /**
   * Nota da mostrare al fiduciario: nasconde il boilerplate della richiesta iniziale
   * (già rappresentato dall'header di pagina) e mostra le sole note di richiesta modifica del BO.
   */
  displayStepNote = computed(() => {
    const note = this.stepNote();
    if (!note) return null;
    return note.trim() === ProfessionalsMfaComponent.MFA_REQUEST_NOTE ? null : note;
  });

  sols = signal<MfaSolForm[]>([]);

  /**
   * Vista sola lettura del riepilogo MFA. Vale sia per l'embed nel Back Office (readonly input)
   * sia per il fiduciario che, dal progression stepper, apre la pagina per rivedere i dati già
   * inviati: la fase non è più editabile (PENDING o step successivi) ma i dati esistono.
   */
  showReadonlyRecap = computed(() => this.readonly() || (!this.editable() && this.sols().length > 0));

  currentStepIndex = signal<0 | 1 | 2>(0);
  submitted = signal<boolean>(false);
  /**
   * Mail (normalizzata) segnalata dal backend come già presente nel pool globale a DB
   * (409 DUPLICATE_RESOURCE). Usata per evidenziare il campo esatto in errore dopo il salvataggio.
   */
  private serverDuplicateEmail = signal<string | null>(null);
  /** Messaggio per screen reader (WCAG 2.1 4.1.3) annunciato al cambio di step. */
  liveMessage = signal<string>('');

  // Decodifiche di dominio (codice → etichetta): il BE espone i codici grezzi, come nel BO.
  /** Tipo via/toponimo (dominio PLACE_NAME): codice → descrizione (es. "V" → "Via"). */
  private streetTypeMap = signal<Map<string, string>>(new Map());
  /** Regione (territorio): codice → nome (es. "LAZ" → "Lazio"). */
  private regionMap = signal<Map<string, string>>(new Map());
  /** Provincia (territorio): codice → nome (es. "RM" → "Roma"). */
  private provinceMap = signal<Map<string, string>>(new Map());
  /** Comune (territorio): codice → nome (es. "H501" → "Roma"). Popolato per le province delle sedi mostrate. */
  private municipalityMap = signal<Map<string, string>>(new Map());

  readonly stepTitles = [
    'Dati di accesso Del Legale Rappresentante',
    'Associazioni collaboratori / impiegati',
    'Riepilogo e conferma',
  ];

  /** Testo informativo (tooltip + aria) sui dati richiesti per ogni codice SOL. */
  readonly mfaFieldsHint =
    "Compilare con i dati di accesso del Legale Rappresentante ai sistemi gestionali AXA. Nome, cognome e codice fiscale devono riferirsi alla persona che ha in carico l'indirizzo email indicato per accedere a questo codice SOL. La mail deve essere PERSONALE, riferita alla singola persona, e non una casella generica/aziendale.";

  // ── Editabilità per codice SOL ────────────────────────────────────────────
  // Editabilità distinta per i due step:
  //  • Step 1 (dati di accesso del legale rappr.): solo i SOL TO_ACTIVATE. I SOL già
  //    attivi hanno la mail principale/responsabile in sola lettura.
  //  • Step 2 (associazioni): anche i SOL ACTIVE partecipano, ma solo per AGGIUNGERE
  //    nuove associazioni; quelle già validate (locked) restano bloccate. Serve al
  //    primo impianto (SOL già attivi a cui agganciare collaboratori/impiegati).

  /** True se i dati principali del SOL (step 1) sono compilabili dal fiduciario. */
  isSolStep1Editable(sol: MfaSolForm): boolean {
    return sol.status === 'TO_ACTIVATE';
  }

  /** True se al SOL (step 2) si possono aggiungere associazioni. */
  isSolStep2Editable(sol: MfaSolForm): boolean {
    return sol.status === 'TO_ACTIVATE' || sol.status === 'ACTIVE';
  }

  /** Codici SOL con dati principali (step 1) da validare/salvare. */
  private step1EditableSols(): MfaSolForm[] {
    return this.sols().filter((s) => this.isSolStep1Editable(s));
  }

  /** Codici SOL con associazioni (step 2) da validare/salvare. */
  private step2EditableSols(): MfaSolForm[] {
    return this.sols().filter((s) => this.isSolStep2Editable(s));
  }

  // ── Validazione ─────────────────────────────────────────────────────────
  // NB: sono metodi (non computed): i campi usano [(ngModel)] su proprietà degli oggetti
  // dentro il signal `sols`, che muta in place senza fare `sols.set(...)`. Un computed
  // resterebbe memoizzato allo stato di caricamento e non reagirebbe alla digitazione; un
  // metodo viene invece rivalutato a ogni ciclo di change detection e riflette l'input reale.

  /** Dati principali (step 1) validi per un singolo SOL: campi presenti, formato ok, mail non duplicata. */
  solStep0Valid(sol: MfaSolForm): boolean {
    return (
      ProfessionalsMfaComponent.EMAIL_RE.test(sol.principalEmail.trim()) &&
      !this.principalDuplicateMsg(sol) &&
      !!sol.holderFirstName.trim() &&
      !!sol.holderLastName.trim() &&
      ProfessionalsMfaComponent.FISCAL_CODE_RE.test(sol.holderFiscalCode.trim())
    );
  }

  /**
   * Associazioni (step 2) valide per un singolo SOL: ogni persona NUOVA selezionata ha mail valida e
   * non duplicata. Le associazioni già validate (locked) sono in sola lettura e non partecipano.
   */
  solStep1Valid(sol: MfaSolForm): boolean {
    return sol.associations
      .filter((a) => a.selected && !a.locked)
      .every(
        (a) =>
          ProfessionalsMfaComponent.EMAIL_RE.test(a.accessEmail.trim()) &&
          !this.accessDuplicateMsg(a),
      );
  }

  /**
   * Step 1 valido: dati di accesso completi per ogni SOL compilabile. Se non ci sono SOL
   * compilabili (es. primo impianto: tutti già attivi) non c'è nulla da compilare → valido.
   */
  step0Valid(): boolean {
    return this.step1EditableSols().every((s) => this.solStep0Valid(s));
  }

  step1Valid(): boolean {
    return this.step2EditableSols().every((s) => this.solStep1Valid(s));
  }

  /**
   * Occorrenze delle mail nel form (trim + case-insensitive), separate per step così da poter
   * applicare la priorità "step «Dati di accesso Del Legale Rappresentante» > step «Associazioni»" nei conflitti incrociati:
   * - `principals`: quante mail principali (step 1) editabili usano ciascuna chiave;
   * - `accesses`:  quante mail di accesso (step 2) editabili e selezionate usano ciascuna chiave;
   * - `reserved`:  mail dei SOL già attivi (sola lettura), che il backend rifiuterebbe come già a DB.
   * Le mail dei soli SOL attivi tra loro NON bloccano. La collisione col pool globale (altri
   * fiduciari) resta rilevata dal backend al salvataggio.
   */
  private emailOccurrences(): {
    principals: Map<string, number>;
    accesses: Map<string, number>;
    reserved: Set<string>;
  } {
    const principals = new Map<string, number>();
    const accesses = new Map<string, number>();
    const reserved = new Set<string>();
    for (const s of this.sols()) {
      const step1Editable = this.isSolStep1Editable(s);
      const step2Editable = this.isSolStep2Editable(s);
      const pk = (s.principalEmail ?? '').trim().toLowerCase();
      if (pk) {
        // Mail principale: conteggiata se editabile (step 1), altrimenti riservata (SOL già attivo).
        if (step1Editable) principals.set(pk, (principals.get(pk) ?? 0) + 1);
        else reserved.add(pk);
      }
      for (const a of s.associations) {
        if (!a.selected) continue;
        const ak = (a.accessEmail ?? '').trim().toLowerCase();
        if (!ak) continue;
        // Solo le associazioni NUOVE (editabili e non bloccate) concorrono ai conflitti; quelle
        // già validate (locked) o su SOL non editabili sono mail riservate, non riassegnabili.
        if (step2Editable && !a.locked) accesses.set(ak, (accesses.get(ak) ?? 0) + 1);
        else reserved.add(ak);
      }
    }
    return { principals, accesses, reserved };
  }

  /**
   * Messaggio di duplicato (bloccante) per una mail principale (step «Dati di accesso»), o null se ok.
   * Priorità allo step 1: la principale NON è un errore se collide solo con una mail di accesso
   * (step 2) — in quel caso è la mail della persona a dover cambiare (vedi {@link accessDuplicateMsg}).
   * La collisione cross-step è comunque evidenziata come avviso (vedi {@link principalDuplicateWarning}).
   */
  private principalDuplicateMsg(sol: MfaSolForm): string | null {
    const key = (sol.principalEmail ?? '').trim().toLowerCase();
    if (!key) return null;
    const { principals, reserved } = this.emailOccurrences();
    if (reserved.has(key)) return ProfessionalsMfaComponent.DUP_ACTIVE_MSG;
    if ((principals.get(key) ?? 0) > 1) return ProfessionalsMfaComponent.DUP_PRINCIPAL_MSG;
    return null;
  }

  /**
   * Avviso (giallo, non bloccante) sullo step 1: la mail principale è usata anche come mail di accesso
   * di una persona (step 2). Serve solo a dare evidenza che è quella la mail in conflitto; la correzione
   * avviene nello step 2 (dove la stessa mail è segnalata come errore), quindi non blocca l'avanzamento.
   */
  principalDuplicateWarning(sol: MfaSolForm): string | null {
    const key = (sol.principalEmail ?? '').trim().toLowerCase();
    if (!key || this.principalDuplicateMsg(sol)) return null;
    const { accesses } = this.emailOccurrences();
    if ((accesses.get(key) ?? 0) > 0) {
      return `Questa mail è usata anche come mail di accesso di una persona (step 2 «${this.stepTitles[1]}»).`;
    }
    return null;
  }

  /** Messaggio di duplicato per una mail di accesso (step «Associazioni»), o null se ok. */
  private accessDuplicateMsg(assoc: MfaAssocForm): string | null {
    if (!assoc.selected || assoc.locked) return null;
    const key = (assoc.accessEmail ?? '').trim().toLowerCase();
    if (!key) return null;
    const { principals, accesses, reserved } = this.emailOccurrences();
    if (reserved.has(key)) return ProfessionalsMfaComponent.DUP_ACTIVE_MSG;
    // Conflitto incrociato: la mail coincide con una principale (step 1, prioritario) → cambia questa.
    if ((principals.get(key) ?? 0) > 0) {
      return `Questa mail è già usata come mail principale di un codice SOL (step 1 «${this.stepTitles[0]}»).`;
    }
    if ((accesses.get(key) ?? 0) > 1) return ProfessionalsMfaComponent.DUP_PEOPLE_MSG;
    return null;
  }

  private noDuplicateEmails(): boolean {
    const noPrincipalDup = this.step1EditableSols().every((s) => !this.principalDuplicateMsg(s));
    const noAccessDup = this.step2EditableSols().every(
      (s) => s.associations.every((a) => !this.accessDuplicateMsg(a)),
    );
    return noPrincipalDup && noAccessDup;
  }

  canConfirm(): boolean {
    return this.step0Valid() && this.step1Valid() && this.noDuplicateEmails();
  }

  // ── Stato/badge delle sezioni (per SOL, per step) ─────────────────────────
  /**
   * Stato di compilazione di una sezione (step 0 = dati principali, step 1 = associazioni):
   * 'error' se ha un problema dopo un tentativo (o mail già a DB), 'complete' se tutti i dati sono
   * ok, 'todo' se ancora da compilare. Guida sia il badge sia l'apertura automatica su errore.
   */
  sectionState(sol: MfaSolForm, step: 0 | 1): 'complete' | 'todo' | 'error' {
    const valid = step === 0 ? this.solStep0Valid(sol) : this.solStep1Valid(sol);
    // Il badge riflette gli errori realmente visibili sui campi (mail già a DB, ripetuta nel form
    // o formato non valido dopo blur/submit) più le omissioni segnalate al submit: così "Errore"
    // non compare mai senza un messaggio a video, né la sezione ricade su "Da compilare" quando
    // in realtà ha un dato errato.
    if (this.sectionHasFieldError(sol, step) || (this.submitted() && !valid)) {
      return 'error';
    }
    return valid ? 'complete' : 'todo';
  }

  /** True se un campo della sezione mostra al momento un messaggio d'errore. */
  private sectionHasFieldError(sol: MfaSolForm, step: 0 | 1): boolean {
    if (step === 0) return !!this.principalEmailError(sol);
    return sol.associations.some((a) => !!this.accessEmailError(a));
  }

  /** Etichetta del badge di sezione mostrata nell'header collassabile. */
  sectionBadgeLabel(sol: MfaSolForm, step: 0 | 1): string {
    switch (this.sectionState(sol, step)) {
      case 'complete': return 'Completato';
      case 'error': return 'Errore';
      default: return 'Da compilare';
    }
  }

  /** Severity PrimeNG del badge di sezione (colori WCAG gestiti via SCSS dove serve). */
  sectionBadgeSeverity(sol: MfaSolForm, step: 0 | 1): 'success' | 'danger' | 'warn' {
    switch (this.sectionState(sol, step)) {
      case 'complete': return 'success';
      case 'error': return 'danger';
      default: return 'warn';
    }
  }

  /** La sezione è aperta se non collassata dall'utente, ma resta forzatamente aperta in errore. */
  isSectionOpen(sol: MfaSolForm, step: 0 | 1): boolean {
    if (this.sectionState(sol, step) === 'error') return true;
    return step === 0 ? !sol.collapsed0 : !sol.collapsed1;
  }

  /** Collassa/espande manualmente la sezione (ignorato di fatto quando è in errore). */
  toggleSection(sol: MfaSolForm, step: 0 | 1): void {
    if (step === 0) sol.collapsed0 = !sol.collapsed0;
    else sol.collapsed1 = !sol.collapsed1;
  }

  /** La card del riepilogo (step 3) è aperta se non collassata dall'utente. */
  isRecapOpen(sol: MfaSolForm): boolean {
    return !sol.collapsed2;
  }

  /** Collassa/espande manualmente la card del riepilogo (step 3). */
  toggleRecap(sol: MfaSolForm): void {
    sol.collapsed2 = !sol.collapsed2;
  }

  /**
   * Al (ri)flag di un'associazione: se la mail è già valorizzata (conservata da una
   * selezione precedente) la marchiamo come "touched" così la validazione di formato
   * scatta subito, senza attendere un nuovo blur o il submit.
   */
  onAssocSelectionChange(assoc: MfaAssocForm): void {
    if (assoc.selected && (assoc.accessEmail ?? '').trim().length > 0) {
      assoc.accessEmailTouched = true;
    }
  }

  // ── Ricerca/filtro persone (step 2) ───────────────────────────────────────
  /** Persone visibili nello step 2 per un SOL, applicando ricerca e filtro "solo selezionati". */
  visibleAssociations(sol: MfaSolForm): MfaAssocForm[] {
    const term = sol.search.trim().toLowerCase();
    return sol.associations.filter((a) => {
      if (sol.onlySelected && !a.selected) return false;
      if (!term) return true;
      return `${a.firstName} ${a.lastName} ${a.fiscalCode ?? ''}`.toLowerCase().includes(term);
    });
  }

  ngOnInit(): void {
    // In embed readonly non tocchiamo il titolo della pagina (lo gestisce la review host).
    if (!this.professionalIdInput()) {
      this.titleService.setTitle('Dati MFA (Codici SOL) - PalEasy');
    }

    // Dizionari di decodifica (indipendenti dal fiduciario): tipo via + geografia.
    this.loadDomainMaps();

    const id = this.professionalIdInput() ?? this.mockAuthService.getDemoProfessionalId();
    if (!id) {
      this.loadError.set(true);
      this.isLoading.set(false);
      this.messageService.add({
        severity: 'warn',
        summary: 'ID mancante',
        detail: 'Nessun ID fiduciario disponibile. Effettua nuovamente l\'accesso.',
        life: 5000,
      });
      return;
    }
    this.professionalId.set(id);
    this.loadData(id);
  }

  /** Carica le mappe di decodifica codice → etichetta per tipo via (PLACE_NAME) e geografia. */
  private loadDomainMaps(): void {
    this.formDropdownService.getFormDropdownValues('PLACE_NAME', 'PLACE_NAME').subscribe({
      next: (res) => {
        const map = new Map<string, string>();
        for (const item of res?.data ?? []) {
          map.set(item.valueKey, item.valueDescription);
        }
        this.streetTypeMap.set(map);
      },
      error: () => this.streetTypeMap.set(new Map()),
    });
    this.geographyService.getRegions('IT').subscribe({
      next: (regions) =>
        this.regionMap.set(new Map((regions ?? []).map((r) => [r.code, r.name]))),
      error: () => this.regionMap.set(new Map()),
    });
    this.geographyService.getProvinces().subscribe({
      next: (provinces) =>
        this.provinceMap.set(new Map((provinces ?? []).map((p) => [p.code, p.name]))),
      error: () => this.provinceMap.set(new Map()),
    });
  }

  /**
   * Popola la mappa comune (codice → nome) per le sedi fisiche mostrate: i comuni
   * si caricano per provincia, quindi richiediamo solo le province effettivamente
   * presenti tra le sedi dei SOL. Il codice comune è nazionalmente univoco, perciò
   * una singola mappa piatta è sufficiente anche con più province.
   */
  private loadMunicipalityNames(forms: MfaSolForm[]): void {
    const provinceCodes = new Set(
      forms
        .filter((f) => f.physicalSite && f.site?.province)
        .map((f) => f.site!.province!.trim())
        .filter(Boolean),
    );
    if (provinceCodes.size === 0) return;
    forkJoin(
      [...provinceCodes].map((pc) => this.geographyService.getMunicipalitiesCached(pc)),
    ).subscribe({
      next: (results) => {
        const map = new Map(this.municipalityMap());
        for (const municipalities of results) {
          for (const m of municipalities ?? []) map.set(m.code, m.name);
        }
        this.municipalityMap.set(map);
      },
      error: () => {
        /* Fallback silenzioso: formatSiteAddress mostra il codice grezzo. */
      },
    });
  }

  private loadData(professionalId: string): void {
    this.isLoading.set(true);
    this.loadError.set(false);

    forkJoin({
      step: this.registrationStepService.getCurrentStep(professionalId),
      mfa: this.solCodeService.getMfaData(professionalId),
      people: this.solCodeService.getMfaEligiblePeople(professionalId),
      // Dati generali del fiduciario per precompilare il responsabile con il titolare.
      // Resiliente: un errore qui non deve impedire il caricamento dei dati MFA.
      general: this.professionalsService
        .getProfessionalGeneralData(professionalId)
        .pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ step, mfa, people, general }) => {
        // La compilazione è consentita solo se lo step corrente è MFA_SETUP in USER_INPUT.
        const isEditable = step?.step === 'MFA_SETUP' && step?.status === 'USER_INPUT';
        this.editable.set(isEditable);
        this.stepNote.set(step?.comments ?? null);

        const eligible = people?.data ?? [];
        const titolare = this.extractTitolare(general);
        // Ordine stabile e indipendente dall'ultima modifica: master sempre primo,
        // poi gli altri codici in ordine alfanumerico (naturale) per codice SOL.
        const forms = (mfa?.data ?? [])
          .map((s) => this.toForm(s, eligible, titolare))
          .sort((a, b) => {
            if (a.master !== b.master) return a.master ? -1 : 1;
            return (a.code ?? '').localeCompare(b.code ?? '', undefined, {
              numeric: true,
              sensitivity: 'base',
            });
          });
        this.sols.set(forms);
        this.isLoading.set(false);
        this.loadMunicipalityNames(forms);
      },
      error: (err) => {
        console.error('Errore caricamento dati MFA:', err);
        this.loadError.set(true);
        this.isLoading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare i dati MFA. Riprova più tardi.',
          life: 5000,
        });
      },
    });
  }

  /** Costruisce lo stato editabile di un SOL unendo i dati salvati con l'elenco persone eleggibili. */
  private toForm(sol: SolCodeMfa, eligible: MfaPerson[], titolare: TitolareData): MfaSolForm {
    const savedById = new Map(sol.associations.map((a) => [a.collaboratorId, a]));
    // Step 2: nome/cognome/cod. fiscale delle persone precompilati dall'anagrafica
    // di collaboratori / impiegati amministrativi (sola lettura, non modificabili qui).
    const associations: MfaAssocForm[] = eligible.map((p) => {
      const saved = savedById.get(p.collaboratorId);
      return {
        collaboratorId: p.collaboratorId,
        firstName: p.firstName,
        lastName: p.lastName,
        fiscalCode: p.fiscalCode,
        role: p.role,
        kind: p.kind,
        selected: !!saved,
        accessEmail: saved?.accessEmail ?? '',
        accessEmailTouched: false,
        // Associazione già validata (ACTIVE) = bloccata: solo lettura, non deselezionabile.
        locked: saved?.status === 'ACTIVE',
      };
    });

    // Step 1 (dati principali): collassato/sola lettura se il SOL non è più TO_ACTIVATE.
    const collapsed0 = sol.status !== 'TO_ACTIVATE';
    // Step 2 (associazioni): espanso quando è possibile aggiungerne (TO_ACTIVATE o ACTIVE),
    // così il primo impianto vede subito l'elenco per agganciare collaboratori/impiegati.
    const collapsed1 = !(sol.status === 'TO_ACTIVATE' || sol.status === 'ACTIVE');

    // Step 1: responsabile precompilato con i dati del titolare quando non ancora salvati.
    return {
      solCodeId: sol.solCodeId,
      code: sol.code,
      master: sol.master,
      status: sol.status,
      denomination: sol.denomination ?? null,
      physicalSite: sol.physicalSite,
      region: sol.region ?? null,
      province: sol.province ?? null,
      site: sol.site ?? null,
      principalEmail: sol.principalEmail ?? '',
      holderFirstName: sol.principalHolderFirstName || titolare.firstname,
      holderLastName: sol.principalHolderLastName || titolare.lastname,
      holderFiscalCode: sol.principalHolderFiscalCode || titolare.fiscalCode,
      associations,
      collapsed0,
      collapsed1,
      collapsed2: false,
      search: '',
      onlySelected: false,
      principalEmailTouched: false,
    };
  }

  /**
   * Estrae i dati anagrafici del titolare dai dati generali del fiduciario.
   * Il titolare di riferimento è il rappresentante legale; la registrazione resta il fallback.
   */
  private extractTitolare(general: any): TitolareData {
    const gd = general?.data ?? general ?? {};
    const company = gd.companyData ?? {};
    const rep = gd.legalRepresentative ?? {};
    return {
      firstname: rep.firstname || company.firstname || gd.firstname || '',
      lastname: rep.lastname || company.lastname || gd.lastname || '',
      fiscalCode: rep.fiscalCode || company.fiscalCode || '',
    };
  }

  // ── Helper di validazione per il template ─────────────────────────────────
  isEmail(value: string): boolean {
    return ProfessionalsMfaComponent.EMAIL_RE.test((value ?? '').trim());
  }

  invalidHolderFirstName(sol: MfaSolForm): boolean {
    return this.submitted() && !sol.holderFirstName.trim();
  }

  invalidHolderLastName(sol: MfaSolForm): boolean {
    return this.submitted() && !sol.holderLastName.trim();
  }

  isFiscalCode(value: string): boolean {
    return ProfessionalsMfaComponent.FISCAL_CODE_RE.test((value ?? '').trim());
  }

  invalidHolderFiscalCode(sol: MfaSolForm): boolean {
    return this.submitted() && !this.isFiscalCode(sol.holderFiscalCode);
  }

  /** Messaggio d'errore contestuale per il codice fiscale responsabile. */
  fiscalCodeError(sol: MfaSolForm): string {
    return !sol.holderFiscalCode.trim()
      ? 'Campo obbligatorio.'
      : 'Codice fiscale non valido (16 caratteri).';
  }

  /** True se questa mail è quella segnalata dal backend come già presente a DB. */
  private isServerDuplicate(email: string): boolean {
    const flagged = this.serverDuplicateEmail();
    return !!flagged && (email ?? '').trim().toLowerCase() === flagged;
  }

  /** Errore da mostrare sotto la mail del codice SOL: già a DB, formato non valido o duplicata nel form. */
  principalEmailError(sol: MfaSolForm): string | null {
    if (this.isServerDuplicate(sol.principalEmail)) return ProfessionalsMfaComponent.DB_DUPLICATE_EMAIL_MSG;
    // La ripetizione nel form è un errore rilevabile subito: la segnaliamo anche prima del submit.
    const dup = this.principalDuplicateMsg(sol);
    if (dup) return dup;
    // Il formato non valido si mostra dopo il submit o all'uscita dal campo (blur).
    if ((this.submitted() || sol.principalEmailTouched) && !this.isEmail(sol.principalEmail)) {
      return 'Inserisci una mail valida.';
    }
    return null;
  }

  /** Errore da mostrare sotto la mail di accesso della persona: già a DB, formato non valido o duplicata nel form. */
  accessEmailError(assoc: MfaAssocForm): string | null {
    if (!assoc.selected || assoc.locked) return null;
    if (this.isServerDuplicate(assoc.accessEmail)) return ProfessionalsMfaComponent.DB_DUPLICATE_EMAIL_MSG;
    // La ripetizione nel form è un errore rilevabile subito: la segnaliamo anche prima del submit.
    const dup = this.accessDuplicateMsg(assoc);
    if (dup) return dup;
    // Il formato non valido si mostra dopo il submit o all'uscita dal campo (blur).
    if ((this.submitted() || assoc.accessEmailTouched) && !this.isEmail(assoc.accessEmail)) {
      return 'Inserisci una mail valida.';
    }
    return null;
  }

  /**
   * True se una mail principale editabile è realmente in conflitto (ripetuta tra SOL o collisione
   * con un SOL attivo). NON considera i conflitti incrociati con lo step «Associazioni»: quelli si
   * risolvono cambiando la mail della persona (step 2), quindi non devono bloccare l'uscita da step 1.
   */
  private hasPrincipalEmailDuplicate(): boolean {
    return this.step1EditableSols().some((s) => !!this.principalDuplicateMsg(s));
  }

  /** Messaggio di avviso che indica esplicitamente quali mail risultano ripetute. */
  private duplicateEmailWarning(): string {
    const dups = new Set<string>();
    for (const s of this.step1EditableSols()) {
      if (this.principalDuplicateMsg(s)) dups.add(s.principalEmail.trim().toLowerCase());
    }
    for (const s of this.step2EditableSols()) {
      for (const a of s.associations) {
        if (this.accessDuplicateMsg(a)) dups.add(a.accessEmail.trim().toLowerCase());
      }
    }
    const list = [...dups];
    const suffix = 'Ogni codice SOL e ogni persona associata deve avere una mail diversa.';
    if (list.length === 1) {
      return `La mail "${list[0]}" è in conflitto. ${suffix}`;
    }
    return `Alcune mail sono in conflitto (${list.join(', ')}). ${suffix}`;
  }

  selectedCount(sol: MfaSolForm): number {
    return sol.associations.filter((a) => a.selected).length;
  }

  /** Etichetta unica del tipo persona (evita la ridondanza "Impiegato · impiegato amministrativo"). */
  personKindLabel(assoc: MfaAssocForm): string {
    return assoc.kind === 'EMPLOYEE' ? 'Impiegato amministrativo' : 'Collaboratore';
  }

  /** Ruolo da mostrare accanto al tipo persona: solo se aggiunge informazione (non per gli impiegati). */
  personRole(assoc: MfaAssocForm): string | null {
    return assoc.kind === 'EMPLOYEE' ? null : assoc.role;
  }

  /** Nominativo in formato "Cognome Nome". */
  personFullName(assoc: MfaAssocForm): string {
    return `${assoc.lastName} ${assoc.firstName}`.trim();
  }

  /**
   * Descrizione della sede a cui è associato il SOL, per aiutare il fiduciario a riconoscerlo:
   * indirizzo fisico (tipo via decodificato, sigla provincia lasciata così com'è) per i SOL su
   * sede fisica; regione e provincia decodificate per i SOL su territorio.
   */
  solLocation(sol: MfaSolForm): string | null {
    if (sol.physicalSite) {
      return sol.site ? this.formatSiteAddress(sol.site) : null;
    }
    const region = this.decodeGeo(this.regionMap(), sol.region);
    const province = this.decodeGeo(this.provinceMap(), sol.province);
    if (region && province) return `${region} — ${province}`;
    return region || province || null;
  }

  /** Icona della sede: segnaposto per la sede fisica, mappa per il territorio operativo. */
  solLocationIcon(sol: MfaSolForm): string {
    return sol.physicalSite ? 'pi-map-marker' : 'pi-map';
  }

  /** Etichetta (screen reader) che distingue la sede fisica dal territorio operativo. */
  solLocationLabel(sol: MfaSolForm): string {
    return sol.physicalSite ? 'Sede associata: ' : 'Territorio operativo: ';
  }

  /** Indirizzo leggibile della sede: decodifica il tipo via, mantiene la sigla provincia grezza. */
  private formatSiteAddress(site: OperationalSite): string | null {
    const streetTypeLabel = site.streetType
      ? this.streetTypeMap().get(site.streetType) ?? site.streetType
      : '';
    const street = [streetTypeLabel, site.streetName, site.streetNumber]
      .map((v) => v?.trim())
      .filter(Boolean)
      .join(' ');
    const municipalityLabel = this.decodeGeo(this.municipalityMap(), site.municipality);
    const locality = [site.zipCode?.trim(), municipalityLabel].filter(Boolean).join(' ');
    const withProvince = site.province?.trim() ? `${locality} (${site.province.trim()})` : locality;
    const full = [street, withProvince].filter(Boolean).join(', ');
    return full || (site.legalFallback ? 'Coincide con la sede legale' : null);
  }

  /** Decodifica difensiva codice → etichetta: se il valore è già leggibile, lo restituisce invariato. */
  private decodeGeo(map: Map<string, string>, value: string | null): string | null {
    const raw = value?.trim();
    if (!raw) return null;
    return map.get(raw) ?? raw;
  }

  statusLabel(status: SolCodeStatus): string {
    switch (status) {
      case 'TO_ACTIVATE': return 'Da attivare';
      case 'ACTIVE': return 'Attivo';
      case 'SUSPENDED': return 'Sospeso';
      case 'INACTIVE': return 'Disattivo';
      default: return status;
    }
  }

  // ── Navigazione step ──────────────────────────────────────────────────────
  goToStep(index: 0 | 1 | 2): void {
    this.currentStepIndex.set(index);
    this.liveMessage.set(`Passo ${index + 1} di ${this.stepTitles.length}: ${this.stepTitles[index]}`);
    this.scrollToTop();
  }

  /**
   * Riporta in cima alla pagina. Lo scroll effettivo avviene sul contenitore
   * #main-content (.content-area ha overflow-y: auto), non sul window, quindi
   * scrolliamo quel container con fallback al window.
   */
  private scrollToTop(): void {
    const container = document.getElementById('main-content');
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  next(): void {
    const idx = this.currentStepIndex();
    if (idx === 0) {
      this.submitted.set(true);
      // Solo i conflitti "propri" dello step 1 (mail principale ripetuta o su SOL attivo) bloccano
      // l'avanzamento: i conflitti incrociati con lo step 2 si risolvono là, quindi si può proseguire.
      if (this.hasPrincipalEmailDuplicate()) {
        this.warn(this.duplicateEmailWarning());
        return;
      }
      if (!this.step0Valid()) {
        this.warn('Completa i dati di accesso (mail, nome, cognome e codice fiscale) per ogni codice SOL.');
        return;
      }
      this.submitted.set(false);
      this.goToStep(1);
    } else if (idx === 1) {
      this.submitted.set(true);
      if (!this.step1Valid()) {
        this.warn('Inserisci una mail di accesso valida per ogni persona associata.');
        return;
      }
      if (!this.noDuplicateEmails()) {
        this.warn(this.duplicateEmailWarning());
        return;
      }
      this.submitted.set(false);
      this.goToStep(2);
    }
  }

  back(): void {
    const idx = this.currentStepIndex();
    if (idx > 0) {
      this.goToStep((idx - 1) as 0 | 1 | 2);
    }
  }

  private warn(detail: string): void {
    this.messageService.add({ severity: 'warn', summary: 'Attenzione', detail, life: 4000 });
  }

  /**
   * Gestisce gli errori di salvataggio/conferma. Se il backend segnala una mail già presente a DB
   * (409 DUPLICATE_RESOURCE), estrae la mail in conflitto, porta l'utente allo step e al campo
   * esatti e li evidenzia; altrimenti mostra un messaggio d'errore generico.
   */
  private handleSaveError(err: HttpErrorResponse, fallback: string): void {
    const body = err?.error as { errorCode?: string; message?: string; errors?: Record<string, string> } | null;
    const email = this.extractDuplicateEmail(body);
    if (email) {
      this.serverDuplicateEmail.set(email);
      const location = this.locateEmailInForm(email);
      if (location) {
        this.submitted.set(true);
        // Apri la sezione del SOL e, allo step 2, azzera i filtri così il campo è visibile.
        if (location.step === 0) location.sol.collapsed0 = false;
        else {
          location.sol.collapsed1 = false;
          location.sol.search = '';
          location.sol.onlySelected = false;
        }
        this.goToStep(location.step);
        this.focusField(location.elementId);
        this.warn(`La mail "${email}" è già registrata nel sistema: è evidenziata nel form, sostituiscila con una diversa.`);
      } else {
        // La mail non è tra quelle correnti del form (caso limite): messaggio comunque puntuale.
        this.messageService.add({
          severity: 'error',
          summary: 'Mail già registrata',
          detail: `La mail "${email}" è già usata nel sistema per un altro codice SOL o accesso: usane una diversa.`,
          life: 7000,
        });
      }
      return;
    }
    this.messageService.add({
      severity: 'error',
      summary: 'Errore',
      detail: body?.message || fallback,
      life: 6000,
    });
  }

  /** Estrae la mail in conflitto dalla risposta 409 DUPLICATE_RESOURCE (normalizzata). */
  private extractDuplicateEmail(
    body: { errorCode?: string; message?: string; errors?: Record<string, string> } | null,
  ): string | null {
    if (!body || body.errorCode !== 'DUPLICATE_RESOURCE') return null;
    const raw = body.errors?.['email'] ?? body.message ?? '';
    const match = raw.match(/[^\s@]+@[^\s@]+\.[^\s@]+/);
    return match ? match[0].trim().toLowerCase() : null;
  }

  /** Individua nel form (solo campi editabili) lo step, il SOL e l'id del campo con la mail indicata. */
  private locateEmailInForm(email: string): { step: 0 | 1; sol: MfaSolForm; elementId: string } | null {
    for (const s of this.step1EditableSols()) {
      if (s.principalEmail.trim().toLowerCase() === email) {
        return { step: 0, sol: s, elementId: `pe-${s.solCodeId}` };
      }
    }
    for (const s of this.step2EditableSols()) {
      for (const a of s.associations) {
        if (a.selected && !a.locked && a.accessEmail.trim().toLowerCase() === email) {
          return { step: 1, sol: s, elementId: `assoc-mail-${s.solCodeId}-${a.collaboratorId}` };
        }
      }
    }
    return null;
  }

  /** Porta a fuoco e in vista il campo indicato, dopo il render dello step. */
  private focusField(elementId: string): void {
    setTimeout(() => {
      const el = document.getElementById(elementId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
    }, 150);
  }

  private buildPayload(sol: MfaSolForm): SolCodeMfaSave {
    return {
      principalEmail: sol.principalEmail.trim(),
      principalHolderFirstName: sol.holderFirstName.trim() || null,
      principalHolderLastName: sol.holderLastName.trim() || null,
      principalHolderFiscalCode: sol.holderFiscalCode.trim().toUpperCase() || null,
      // Invia solo le associazioni NUOVE (non bloccate): quelle già validate (ACTIVE) sono
      // preservate dal backend (merge) e non vanno rispedite.
      associations: sol.associations
        .filter((a) => a.selected && !a.locked)
        .map((a) => ({ collaboratorId: a.collaboratorId, accessEmail: a.accessEmail.trim() })),
    };
  }

  /** Salva in bozza tutti i codici SOL (nessun avanzamento di stato). */
  saveDraft(): void {
    const id = this.professionalId();
    if (!id || this.sols().length === 0) return;

    const editable = this.step2EditableSols();
    if (editable.length === 0) return;

    this.isSaving.set(true);
    this.serverDuplicateEmail.set(null);
    forkJoin(editable.map((s) => this.solCodeService.saveMfaData(id, s.solCodeId, this.buildPayload(s)))).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Bozza salvata',
          detail: 'I dati MFA sono stati salvati in bozza.',
          life: 4000,
        });
      },
      error: (err) => {
        this.isSaving.set(false);
        this.handleSaveError(err, 'Impossibile salvare i dati MFA.');
      },
    });
  }

  /** Salva tutti i dati e conferma la compilazione (USER_INPUT → PENDING, revisione BO). */
  confirm(): void {
    const id = this.professionalId();
    if (!id) return;

    this.submitted.set(true);
    if (!this.canConfirm()) {
      // Riporta l'utente allo step con l'errore, con un messaggio che ne indica la causa.
      if (!this.step0Valid()) {
        this.warn('Completa i dati di accesso (mail, nome, cognome e codice fiscale) per ogni codice SOL.');
        this.goToStep(0);
      } else if (!this.step1Valid()) {
        this.warn('Inserisci una mail di accesso valida per ogni persona associata.');
        this.goToStep(1);
      } else {
        // Unica causa rimasta: mail duplicate nel form.
        this.warn(this.duplicateEmailWarning());
        this.goToStep(this.hasPrincipalEmailDuplicate() ? 0 : 1);
      }
      return;
    }

    // Doppia conferma prima dell'invio definitivo al Back Office, come sull'invio dei dati di anagrafica.
    this.confirmDialogService.confirmAction(
      'Confermi di voler inviare i dati MFA al Back Office per la validazione? Dopo l\'invio non potrai più modificarli fino a un\'eventuale richiesta di modifica.',
      () => this.submitMfaData(id),
      'Conferma invio',
      'Sì, invia',
      'Annulla',
    );
  }

  /** Salvataggio + conferma effettivi dei dati MFA (eseguiti dopo la doppia conferma). */
  private submitMfaData(id: string): void {
    this.isConfirming.set(true);
    this.serverDuplicateEmail.set(null);
    // forkJoin([]) non emette: se non c'è nulla da salvare (nessun SOL editabile) si conferma direttamente.
    const toSave = this.step2EditableSols();
    const save$: Observable<unknown> = toSave.length
      ? forkJoin(toSave.map((s) => this.solCodeService.saveMfaData(id, s.solCodeId, this.buildPayload(s))))
      : of(null);
    save$
      .pipe(switchMap(() => this.solCodeService.confirmMfa(id)))
      .subscribe({
        next: () => {
          this.isConfirming.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Dati MFA inviati',
            detail: 'I dati MFA sono stati inviati al Back Office per la validazione.',
            life: 5000,
          });
          setTimeout(() => {
            this.router.navigate(['/axa/professionals-registration-progress']).then(() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            });
          }, 1200);
        },
        error: (err) => {
          this.isConfirming.set(false);
          this.handleSaveError(err, 'Impossibile confermare i dati MFA.');
        },
      });
  }

  goToProgress(): void {
    // In sola lettura non ci sono dati da perdere: naviga senza il warning.
    if (!this.editable()) {
      this.navigateToProgress();
      return;
    }
    // Come su "Dati specifici" in modifica: avverte della perdita dei dati non salvati.
    this.confirmDialogService.confirmAction(
      ProfessionalsMfaComponent.UNSAVED_CHANGES_MESSAGE,
      () => this.navigateToProgress(),
    );
  }

  private navigateToProgress(): void {
    this.router.navigate(['/axa/professionals-registration-progress']).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }
}
